import React, { createContext, type PropsWithChildren } from 'react';

import { type BootstrapContainerProps } from '../index';

import {
	defaultDynamicContext,
	type DynamicBootstrapContextState,
	DynamicContextProvider,
} from './dynamic-context-provider';
import {
	defaultStaticContext,
	type StaticBootstrapContextState,
	StaticContextProvider,
} from './static-context-provider';

/**
 * A replacement for RSS store/bootstrap AKA BootstrapStoreContainer
 * Builds StaticBootstrapContext and DynamicBootstrapContext that provide store-like functionality
 */
export const BootstrapContextProvider = ({
	children,
	...props
}: PropsWithChildren<BootstrapContainerProps>): React.JSX.Element => {
	return (
		<StaticContextProvider {...props}>
			<DynamicContextProvider {...props}>{children}</DynamicContextProvider>
		</StaticContextProvider>
	);
};

/**
 * A context provider for static values from the bootstrap phase
 * Guaranteed to never re-render except:
 * - initial render (will use initialState values)
 * - once when all bootstrap queries/logic has finished (final values)
 *
 * Caution: do not place attributes in here that require mutation after bootstrap phase!
 */
export const StaticBootstrapContext =
	createContext<StaticBootstrapContextState>(defaultStaticContext);

/**
 * A context provider for dynamic values from the bootstrap phase
 *
 * Subscribe with caution and make use of useMemo/useCallback to avoid re-renders
 */
export const DynamicBootstrapContext =
	createContext<DynamicBootstrapContextState>(defaultDynamicContext);
